import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageCircle, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { submitLead } from "@/lib/api";
import { TID } from "@/lib/testIds";

const VEHICLES = [
  { value: "pkw", label: "PKW" },
  { value: "lkw", label: "LKW" },
  { value: "agrar", label: "Agrar / Landmaschine" },
  { value: "other", label: "Sonstiges" },
];

export default function Contact({ config }) {
  const c = config?.contact || {};
  const co = config?.company || {};
  const whatsapp = c.whatsapp && c.whatsapp.replace(/\D/g, "");
  const waHref = whatsapp
    ? `https://wa.me/${whatsapp}?text=${encodeURIComponent("Hallo VPT Engineering, ich interessiere mich für Chiptuning.")}`
    : `mailto:${c.email}`;
  const mailHref = `mailto:${c.email}?subject=${encodeURIComponent("Anfrage Chiptuning")}`;

  const [form, setForm] = useState({ name: "", email: "", vehicle_type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Bitte Name und E-Mail angeben.");
      return;
    }
    setLoading(true);
    try {
      await submitLead(form);
      toast.success("Danke! Wir melden uns schnellstmöglich bei dir.");
      setForm({ name: "", email: "", vehicle_type: "", message: "" });
    } catch (err) {
      toast.error("Senden fehlgeschlagen. Bitte per WhatsApp oder E-Mail kontaktieren.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact-section" data-testid={TID.contact.root} className="py-24 md:py-32 bg-[#070707] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <p className="text-sm uppercase tracking-[0.35em] text-primary mb-4">Kontakt</p>
          <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Sprich mit<br />den <span className="text-primary">Technikern</span>
          </h2>
          <p className="mt-6 max-w-md text-white/55 leading-relaxed">
            Schnellste Antwort per WhatsApp oder E-Mail. Wir beraten dich unverbindlich zu Möglichkeiten für dein Fahrzeug.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              data-testid={TID.contact.whatsapp}
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-black font-display font-bold uppercase tracking-wider px-7 py-4 hover:bg-white transition-colors duration-300"
            >
              <MessageCircle className="h-5 w-5" /> WhatsApp
            </a>
            <a
              data-testid={TID.contact.email}
              href={mailHref}
              className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-display font-bold uppercase tracking-wider px-7 py-4 hover:border-primary hover:text-primary transition-colors duration-300"
            >
              <Mail className="h-5 w-5" /> E-Mail
            </a>
          </div>

          <div className="mt-12 space-y-4 text-sm text-white/55">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <div className="text-white font-semibold">{co.name}</div>
                <div>{co.street}, {co.postal_code} {co.city} ({co.country})</div>
                <div className="mt-1">UID: {co.vat_id}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-primary shrink-0" />
              <a href={mailHref} className="hover:text-primary transition-colors">{c.email}</a>
            </div>
          </div>
        </div>

        <motion.form
          data-testid={TID.contact.form}
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="bg-[#111] border border-white/10 p-7 md:p-10 space-y-6"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="c-name" className="uppercase tracking-widest text-xs text-white/60">Name</Label>
              <Input id="c-name" data-testid={TID.contact.name} value={form.name} onChange={set("name")}
                placeholder="Dein Name" className="bg-black border-white/15 h-12 focus-visible:ring-primary" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-email" className="uppercase tracking-widest text-xs text-white/60">E-Mail</Label>
              <Input id="c-email" type="email" data-testid={TID.contact.emailField} value={form.email} onChange={set("email")}
                placeholder="mail@beispiel.at" className="bg-black border-white/15 h-12 focus-visible:ring-primary" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="uppercase tracking-widest text-xs text-white/60">Fahrzeugtyp</Label>
            <Select value={form.vehicle_type} onValueChange={set("vehicle_type")}>
              <SelectTrigger data-testid={TID.contact.vehicle} className="bg-black border-white/15 h-12 focus:ring-primary">
                <SelectValue placeholder="Bitte wählen" />
              </SelectTrigger>
              <SelectContent>
                {VEHICLES.map((v) => (
                  <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="c-msg" className="uppercase tracking-widest text-xs text-white/60">Nachricht</Label>
            <Textarea id="c-msg" data-testid={TID.contact.message} value={form.message} onChange={set("message")}
              placeholder="Marke, Modell, Motorisierung & dein Ziel…" rows={4}
              className="bg-black border-white/15 focus-visible:ring-primary resize-none" />
          </div>

          <button
            type="submit"
            data-testid={TID.contact.submit}
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-display font-bold uppercase tracking-wider px-8 py-4 hover:bg-white transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            {loading ? "Wird gesendet…" : "Anfrage senden"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
